import React from 'react';
import { i18n } from 'config/locales';

import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { HistoryFederalItemModel } from 'forms/sections/history/federal/types';
import {
  CommonOnUpdate,
  CommonScrollIntoView,
  CommonRequired,
  CommonOnError,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import Field from 'components/Form/Field';
import DateRange from 'components/Form/DateRange';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import Reprimand from 'components/Form/PVQEmployment/Reprimand';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import FederalAgencyAutoComplete from 'components/Form/FederalAgencyAutoComplete';

interface Props
  extends ValidationElementProps,
  Partial<HistoryFederalItemModel>,
  AccordionItemProps {
  onUpdate: CommonOnUpdate
  scrollIntoView: CommonScrollIntoView
  required: CommonRequired
  onError: CommonOnError
  addressBooks: CommonAddressBooks

  // TODO: Remove
  dispatch?: Dispatch
}

export default class FederalItem extends ValidationElement<Props> {
  update = (queue: Partial<HistoryFederalItemModel>) => {
    this.props.onUpdate({
      Dates: this.props.Dates,
      Name: this.props.Name,
      Position: this.props.Position,
      Address: this.props.Address,
      Comments: this.props.Comments,
      Reprimand: this.props.Reprimand,
      ReasonLeft: this.props.ReasonLeft,
      Branch: this.props.Branch,
      ...queue,
    });
  };

  updateDates = (values: HistoryFederalItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updateName = (values: HistoryFederalItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updatePosition = (values: HistoryFederalItemModel['Position']) => {
    this.update({
      Position: values,
    });
  };

  updateAddress = (values: HistoryFederalItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateComments = (values: HistoryFederalItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateReprimand = (values: HistoryFederalItemModel['Reprimand']) => {
    this.update({
      Reprimand: values,
    });
  };

  updateReasonLeft = (values: HistoryFederalItemModel['ReasonLeft']) => {
    this.update({
      ReasonLeft: values,
    });
  };

  updateBranch = (values: HistoryFederalItemModel['Branch']) => {
    if (this.props.addWithData && this.props.Branch?.value !== 'Yes' && values?.value && values?.value === 'Yes') {
      this.update({
        Branch: values,
      });

      this.props.addWithData({ Name: this.props.Name });
    } else {
      this.update({
        Branch: values,
      });
    }
  };

  onFederalAgencySuggestionSelected = (event, options, selectedAgency) => {
    if (selectedAgency) {
      this.update({
        Name: { ...this.props.Name, value: (selectedAgency || '') }
      });
    }
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div className="federal-item">
        <Field
          attribute={`${attribute}.Name`}
          title={i18n.t('history.pvq.federal.heading.name')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
        >
          <FederalAgencyAutoComplete
            name="Name"
            className="federal-agency-auto"
            attribute={`${attribute}.Name`}
            onUpdate={this.updateName}
            onError={this.props.onError}
            required={this.props.required}
            value={this.props.Name?.value}
            warningMessage={i18n.t('history.federal.agencyDropdownWarning')}
            onSuggestionSelected={this.onFederalAgencySuggestionSelected}
          />
        </Field>
        <Field
          attribute={`${attribute}.Dates`}
          title={i18n.t('history.pvq.federal.heading.dates')}
          titleSize="h4"
          help="history.federal.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.Dates`}
            name="Dates"
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            allowPresent={false}
          />
        </Field>
        <Reprimand
          attribute={`${attribute}.Reprimand`}
          name="Reprimand"
          className="reprimand"
          {...this.props.Reprimand}
          Dates={this.props.Dates}
          hideGivenByEmail
          hideGivenByTelephone
          onUpdate={this.updateReprimand}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          appendLabel={i18n.t('history.pvq.employment.default.reprimand.federalAppend')}
        />
        <Field
          attribute={`${attribute}.ReasonLeft`}
          title={i18n.t('history.pvq.federal.heading.reasonLeft')}
          titleSize="h4"
          className="explanation-left"
          required
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.ReasonLeft`}
            name="Text"
            {...this.props.ReasonLeft}
            onUpdate={this.updateReasonLeft}
            onError={this.props.onError}
            maxlength="100"
            required={this.props.required}
          />
        </Field>
        <Branch
          attribute={`${attribute}.Branch`}
          label={i18n.t('history.pvq.federal.heading.additionalEmployment')}
          labelSize="h4"
          value={(this.props.Branch || {}).value}
          onUpdate={this.updateBranch}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
      </div>
    ) : (
      <div className="federal-item">
        <Field
          attribute={`${attribute}.Dates`}
          title={i18n.t('history.federal.heading.dates')}
          titleSize="h4"
          help="history.federal.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.Dates`}
            name="Dates"
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            allowPresent={false}
          />
        </Field>
        <Field
          attribute={`${attribute}.Name`}
          title={i18n.t('history.federal.heading.name')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
        >
          <FederalAgencyAutoComplete
            name="Name"
            className="federal-agency-auto"
            attribute={`${attribute}.Name`}
            onUpdate={this.updateName}
            onError={this.props.onError}
            required={this.props.required}
            value={this.props.Name?.value}
            warningMessage={i18n.t('history.federal.agencyDropdownWarning')}
            onSuggestionSelected={this.onFederalAgencySuggestionSelected}
          />
        </Field>

        <Field
          attribute={`${attribute}.Position`}
          title={i18n.t('history.federal.heading.position')}
          titleSize="h4"
          className="federal-position"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.Position`}
            name="Position"
            {...this.props.Position}
            onUpdate={this.updatePosition}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Address`}
          title={i18n.t('history.federal.heading.address')}
          optional
          help="history.federal.help.address"
          className="federal-agency-address"
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            attribute={`${attribute}.Address`}
            name="Address"
            {...this.props.Address}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Agency"
            showPostOffice
            dispatch={this.props.dispatch}
            required={this.props.required}
          />
        </Field>
        <Comments
          title={i18n.t(`history.federal.heading.comments`)}
          name="Comments"
          attribute={`${attribute}.Comments`}
          className="optional-comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          onError={this.props.onError}
          required={false}
        />
      </div>
    );
  }
}

FederalItem.defaultProps = {
  Comments: {},
  Branch: {},
  addWithData: () => { },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};
